import { IonButton, IonContent, IonIcon, IonPage, IonText } from '@ionic/react'
import { chevronForwardOutline } from 'ionicons/icons'
import { observer } from 'mobx-react'
import moment from 'moment-timezone'
import React, { useContext, useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import { amountString } from '../../../common'
import { Chip, CustomSpacer, DetailContent, PagingContent, StyleIonHeader, TextStyle } from '../../../components'
import { DivideLine, DivideLineBold } from '../../../components/atoms/divide'
import { StoresContext } from '../../../provider/StoreProvider'
import { ProductInfo } from '../components'

type Props = RouteComponentProps<{
  productId: string
}>

export const ProductDetail: React.FC<Props> = observer(({ match }) => {
  const { myStore } = useContext(StoresContext)
  let loadParam = { productId: Number(match.params.productId) }
  const pagingStore = myStore.purchasePagingStore
  const detailStore = myStore.purchaseDetailStore

  useEffect(() => {
    loadParam = { productId: Number(match.params.productId) }
    detailStore.load({ param: loadParam })
    pagingStore.load({ data: loadParam }, true)
  }, [match.params.productId])

  return (
    <IonPage>
      <StyleIonHeader type={'SUB'} title={'자산 상세'} href={'/myPage/asset'} />
      <IonContent>
        <DetailContent detailStore={detailStore}>
          <ProductInfo
            id={detailStore.content?.id ?? 0}
            artist={detailStore.content?.artist ?? '-'}
            title={detailStore.content?.name ?? '-'}
            img={detailStore.content?.imageUri ?? '-'}
          />
          {/* 투표중 */}
          {detailStore.content?.status === 'VOTE' && (
            <>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                  <span className="status">
                    <TextStyle type={'b_13'} color={'error_main'}>
                      {detailStore.content.voteStatus === 'FINISH' ? '투표 종료' : '투표 진행중'}
                      <span className="badge" />
                    </TextStyle>
                  </span>
                </div>
                <IonButton
                  size="small"
                  fill="clear"
                  color="medium"
                  routerLink={`/myPage/asset/${detailStore.content.productVoteId}/disposal`}
                >
                  <span>
                    <TextStyle type={'r_13'}>{detailStore.content.hasVote ? '참여 완료' : '미참여'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
          {/* 투표 종료 - 매각 중 */}
          {detailStore.content?.status === 'SELL' && (
            <>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                  <span className="status">
                    <TextStyle type={'b_13'} color={'error_main'}>
                      매각 진행중
                      <span className="badge" />
                    </TextStyle>
                  </span>
                </div>
                <IonButton
                  size="small"
                  fill="clear"
                  color="medium"
                  routerLink={`/myPage/asset/${detailStore.content.productVoteId}/disposal`}
                >
                  <span>
                    <TextStyle type={'r_13'}>{'투표 결과보기'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
          {/* 매각 완료 - 정산 전 */}
          {detailStore.content?.status === 'FINISH' && detailStore.content.userProductStatus === 'TO_BE_SETTLEMENT' && (
            <>
              <ProductDetailInfo>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>보유 수량</TextStyle>
                  <TextStyle type={'r_15'}>{detailStore.content.count.toLocaleString() ?? 0} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="15px"></CustomSpacer>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>정산 예정 금액</TextStyle>
                  <TextStyle type={'r_15'}>
                    {detailStore.content.expectedRevenueAmount?.toLocaleString() ?? 0} 원
                  </TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="24px" />
                <DivideLine />
              </ProductDetailInfo>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                  <span className="status">
                    <TextStyle type={'b_13'} color={'error_main'}>
                      정산 예정
                      <span className="badge" />
                    </TextStyle>
                  </span>
                </div>
                <IonButton
                  size="small"
                  fill="clear"
                  color="medium"
                  routerLink={`/myPage/asset/${detailStore.content.productVoteId}/disposal`}
                >
                  <span>
                    <TextStyle type={'r_13'}>{'매각대금 정산하기'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
          {/* 매각 완료 - 정산 후 */}
          {detailStore.content?.status === 'FINISH' && detailStore.content.userProductStatus === 'FINISH' && (
            <>
              <ProductDetailInfo>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>정산 금액</TextStyle>
                  <TextStyle type={'r_15'}>{detailStore.content.revenueAmount?.toLocaleString() ?? 0} 원</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="24px" />
              </ProductDetailInfo>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                </div>
                <IonButton
                  size="small"
                  fill="clear"
                  color="medium"
                  routerLink={`/myPage/asset/${detailStore.content.productVoteId}/disposal`}
                >
                  <span>
                    <TextStyle type={'r_13'}>{'정산내역'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
          {/* 트레이드 상태 */}
          {detailStore.content?.status === 'TRADE' && (
            <>
              <ProductDetailInfo>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>현재가</TextStyle>
                  <div className="current-cost">
                    <Chip
                      type={
                        myStore.assetChangeRate != undefined && myStore.assetChangeRate > 0
                          ? 'red'
                          : myStore.assetChangeRate != undefined && myStore.assetChangeRate < 0
                          ? 'blue'
                          : 'gray'
                      }
                    >{`${myStore.assetChangeRate != undefined && myStore.assetChangeRate > 0 ? '+' : ''}${
                      amountString(String(myStore.assetChangeRate), false, 1) ?? 0
                    }%`}</Chip>
                    &nbsp;&nbsp;
                    <TextStyle type={'b_20'}>{detailStore.content.currentCost.toLocaleString() ?? 0} 원</TextStyle>
                  </div>
                </BetweenWrap>
                <CustomSpacer spacing="15px"></CustomSpacer>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>거래 가능 수량</TextStyle>
                  <TextStyle type={'r_15'}>{detailStore.content.count.toLocaleString() ?? 0} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="15px"></CustomSpacer>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>미체결 수량</TextStyle>
                  <TextStyle type={'r_15'}>{detailStore.content.lockedCount?.toLocaleString() ?? 0} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="24px" />
                <DivideLine />
              </ProductDetailInfo>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                </div>
                <IonButton size="small" fill="clear" color="medium">
                  <span>
                    <TextStyle type={'r_13'}>{'소식이 없습니다.'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
          {/* 조각 투자 상태 */}
          {detailStore.content?.status === 'SALE' && (
            <>
              <ProductDetailInfo>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>현재가</TextStyle>
                  <div className="current-cost">
                    <TextStyle type={'b_20'}>{detailStore.content.averageCost.toLocaleString() ?? 0} 원</TextStyle>
                  </div>
                </BetweenWrap>
                <CustomSpacer spacing="15px"></CustomSpacer>
                <BetweenWrap>
                  <TextStyle type={'r_15'}>보유 수량</TextStyle>
                  <TextStyle type={'r_15'}>{detailStore.content.count.toLocaleString() ?? 0} 개</TextStyle>
                </BetweenWrap>
                <CustomSpacer spacing="15px"></CustomSpacer>
                <DivideLine />
              </ProductDetailInfo>
              <DivideLine isShort={true} />
              <DivideLineBold />
              <StatusContainer>
                <div className="left-content-wrap">
                  <TextStyle type={'b_16'}>매각 관련</TextStyle>
                </div>
                <IonButton size="small" fill="clear" color="medium">
                  <span>
                    <TextStyle type={'r_13'}>{'소식이 없습니다.'}</TextStyle>
                  </span>
                  <IonIcon slot="end" icon={chevronForwardOutline} />
                </IonButton>
              </StatusContainer>
            </>
          )}
        </DetailContent>
        <HistoryContainer>
          <DivideLine />
          <HistoryTitleWrap>
            <TextStyle type={'b_16'}>거래내역</TextStyle>
          </HistoryTitleWrap>
          <PagingContent loadParam={loadParam} pagingStore={pagingStore}>
            {pagingStore.list.map((d, i) => (
              <TransactionContainer key={i}>
                <DateContainer>
                  <TextStyle type={'b_11'}>{`${moment(new Date(d.createdDate)).format('YY.MM.DD')}`}</TextStyle>
                  <TextStyle type={'r_11'}>{`${moment(new Date(d.createdDate)).format('HH:MM')}`}</TextStyle>
                </DateContainer>
                <ContentContainer>
                  <BetweenWrap>
                    {d.purchaseType === 'SALE' ? (
                      <TextStyle type={'b_14'} color={'primary_main'}>
                        조각 구매
                      </TextStyle>
                    ) : d.purchaseType === 'TRADE' && d.tradeOrder?.type === 'BUY' ? (
                      <TextStyle type={'b_14'} color={'error_main'}>
                        트레이드(매수)
                      </TextStyle>
                    ) : d.purchaseType === 'TRADE' && d.tradeOrder?.type === 'SELL' ? (
                      <TextStyle type={'b_14'} color={'secondary_main'}>
                        트레이드(매도)
                      </TextStyle>
                    ) : d.purchaseType === 'WALLET_TRANSFER' ? (
                      <TextStyle type={'b_14'} color={'primary_light'}>
                        지갑 이전
                      </TextStyle>
                    ) : d.purchaseType === 'DESTRUCT' ? (
                      <TextStyle type={'b_14'} color={'error_main'}>
                        조각 소각
                      </TextStyle>
                    ) : d.purchaseType === 'REFUND' ? (
                      <TextStyle type={'b_14'}>환불</TextStyle>
                    ) : (
                      '-'
                    )}
                    <TextStyle type={'r_13'}>{Math.abs(d.count).toLocaleString() ?? 0} 개</TextStyle>
                  </BetweenWrap>
                  <BetweenWrap>
                    <IonText />
                    <TextStyle type={'r_13'}>{Math.abs(d.count * d.cost).toLocaleString() ?? 0} 원</TextStyle>
                  </BetweenWrap>
                  <CustomSpacer spacing="16px" />
                </ContentContainer>
              </TransactionContainer>
            ))}
          </PagingContent>
        </HistoryContainer>
        <CustomSpacer spacing="20px" />
      </IonContent>
    </IonPage>
  )
})

const ProductDetailInfo = styled.div`
  padding: 16px 20px 0 20px;
`

const StatusContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  max-height: 50px;
  .left-content-wrap {
    .status {
      position: relative;
      margin-left: 5px;
      .badge {
        position: absolute;
        background-color: red;
        border-radius: 10px;
        width: 4px;
        height: 4px;
        z-index: 10;
        margin-left: 2px;
        bottom: 14px;
      }
    }
  }
  ion-button {
    --padding-end: 0;
    span {
      padding-top: 2px;
    }
  }
`

const HistoryContainer = styled.div`
  padding: 0px 20px;
`

const HistoryTitleWrap = styled.div`
  padding: 15.5px 0;
`

const TransactionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 5fr;
  grid-column-gap: 10px;
  grid-row-gap: 0px;
  margin-top: 16px;
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const BetweenWrap = styled.div`
  display: flex;
  justify-content: space-between;
  .current-cost {
    display: flex;
    align-items: center;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 3px;
  border-bottom: solid 1px var(--ion-color-gray100);
`
